import { SEO } from "@/components/SEO";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { BUSINESS } from "@/data/businessInfo";
import { ServiceCardsSection, LocationCardsSection } from "@/components/SharedSections";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "", city: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    }
    setLoading(false);
  };

  return (
    <div>
      <SEO title="Contact Electrician Port St. Lucie FL | Southern Light Solutions (772) 233-6472" description="Contact Southern Light Solutions LLC for electrical services in Port St. Lucie and the Treasure Coast. Call (772) 233-6472 or submit our form for a free estimate. Licensed, insured, BBB A-rated." path="/contact" />
      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Get In Touch</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact Us</h1>
            <p className="mt-4 text-base lg:text-lg text-[#6B3A1F] max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>Ready to discuss your electrical project? Reach out and we'll respond promptly.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-10 text-center" data-testid="contact-success">
                  <CheckCircle className="w-16 h-16 text-[#317E8C] mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-[#401303] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Message Received</h2>
                  <p className="text-[#6B3A1F] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>We'll get back to you within one business day. For immediate assistance, call us at {BUSINESS.phone}.</p>
                  <Link to="/" className="text-[#BF8D30] font-semibold">Back to Home</Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-6 lg:p-8 space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Full Name *</label>
                      <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none transition-colors duration-200 text-sm" placeholder="Your name" required data-testid="contact-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Email *</label>
                      <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none transition-colors duration-200 text-sm" placeholder="your@email.com" required data-testid="contact-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Phone *</label>
                      <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none transition-colors duration-200 text-sm" placeholder="(772) 000-0000" required data-testid="contact-phone" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>City</label>
                      <input type="text" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none transition-colors duration-200 text-sm" placeholder="Port St. Lucie" data-testid="contact-city" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Service Needed</label>
                    <select value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none transition-colors duration-200 text-sm bg-white" data-testid="contact-service">
                      <option value="">Select a service</option>
                      <option>EV Charger Installation</option>
                      <option>Generator Installation & Repair</option>
                      <option>Electrical Panel Upgrade</option>
                      <option>Lighting Installation</option>
                      <option>Ceiling Fan Installation</option>
                      <option>Code Corrections</option>
                      <option>Electrical Troubleshooting</option>
                      <option>Commercial AV & Display</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#401303] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Message</label>
                    <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={4} className="w-full px-4 py-3 border-2 border-[#6B3A1F]/20 rounded-xl focus:border-[#BF8D30] focus:outline-none transition-colors duration-200 text-sm resize-none" placeholder="Tell us about your project..." data-testid="contact-message" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 btn-gradient rounded-full text-sm disabled:opacity-50" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="contact-submit">
                    <Send className="w-4 h-4" /> {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-[#6B3A1F]/10 p-6">
                <h3 className="text-base font-bold text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact Information</h3>
                <div className="space-y-4">
                  <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-start gap-3 group" data-testid="contact-info-phone">
                    <Phone className="w-5 h-5 text-[#BF8D30] mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#401303] group-hover:text-[#BF8D30] transition-colors duration-200">{BUSINESS.phone}</p>
                      <p className="text-xs text-[#6B3A1F]">Call or text anytime</p>
                    </div>
                  </a>
                  <a href={`mailto:${BUSINESS.email}`} className="flex items-start gap-3 group">
                    <Mail className="w-5 h-5 text-[#BF8D30] mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#401303] group-hover:text-[#BF8D30] transition-colors duration-200">{BUSINESS.email}</p>
                      <p className="text-xs text-[#6B3A1F]">Email us anytime</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#BF8D30] mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#401303]">Serving the Treasure Coast & Palm Beach County</p>
                      <p className="text-xs text-[#6B3A1F]">St. Lucie, Martin, Indian River & Palm Beach Counties</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#BF8D30] mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#401303]">Business Hours</p>
                      <p className="text-xs text-[#6B3A1F]">Open 24 Hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceCardsSection />
      <LocationCardsSection />
    </div>
  );
}